// src/components/tutor/myinfo/TutorSubjectMatrix.js
// Tutor Desktop — category-first two-view navigation
// View 1: category list with qualified count per category
// View 2: subject list for selected category with toggle checkboxes
import React, { useState, useEffect, useMemo } from "react";
import { db, auth } from "../../../firebaseConfig";
import {
  collection,
  query,
  getDoc,
  onSnapshot,
  updateDoc,
  doc,
  arrayUnion,
  arrayRemove,
  orderBy,
} from "firebase/firestore";
import { FiChevronRight, FiChevronLeft, FiSearch, FiX } from "react-icons/fi";
import "./TutorSubjectMatrix.css";

function normalizeCategoryId(name) {
  return (name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

export default function SubjectMatrix() {
  const [currentUser, setCurrentUser] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Navigation state: null = category list, string = selected category name
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  /* ─── Fetch current tutor ─────────────────────────────────────────── */
  useEffect(() => {
    if (!auth.currentUser) return;
    getDoc(doc(db, "users", auth.currentUser.uid)).then((snap) => {
      if (snap.exists()) {
        setCurrentUser({ uid: snap.id, ...snap.data(), subjects: snap.data().subjects || [] });
      }
    });
  }, []);

  /* ─── Real-time categories ───────────────────────────────────────────── */
  useEffect(() => {
    const q = query(collection(db, "subject_categories"), orderBy("order", "asc"));
    return onSnapshot(q, (snap) => {
      const cats = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cats.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(cats);
    });
  }, []);

  /* ─── Real-time subjects ─────────────────────────────────────────────── */
  useEffect(() => {
    return onSnapshot(query(collection(db, "subjects")), (snap) => {
      const list = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().name,
        rootSubject: d.data().rootSubject || "Uncategorized",
        tutorIds: d.data().tutorIds || [],
        order: d.data().order || 0,
        ...d.data(),
      }));
      setSubjects(list);
      setLoading(false);
    });
  }, []);

  /* ─── Toggle assignment ──────────────────────────────────────────────── */
  const toggleAssignment = async (subject) => {
    if (!currentUser) return;
    const assigned =
      subject.tutorIds?.includes(currentUser.uid) ||
      currentUser.subjects?.includes(subject.name);
    const subRef = doc(db, "subjects", subject.id);
    const tutorRef = doc(db, "users", currentUser.uid);
    try {
      if (assigned) {
        await updateDoc(subRef, { tutorIds: arrayRemove(currentUser.uid) });
        await updateDoc(tutorRef, { subjects: arrayRemove(subject.name) });
        setCurrentUser((p) => ({ ...p, subjects: p.subjects.filter((s) => s !== subject.name) }));
      } else {
        await updateDoc(subRef, { tutorIds: arrayUnion(currentUser.uid) });
        await updateDoc(tutorRef, { subjects: arrayUnion(subject.name) });
        setCurrentUser((p) => ({ ...p, subjects: [...(p.subjects || []), subject.name] }));
      }
    } catch (err) {
      console.error("Error toggling:", err);
    }
  };

  /* ─── Computed data ──────────────────────────────────────────────────── */
  const { categoryRows, totalQualified } = useMemo(() => {
    const sortedCats = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    const mainBuckets = new Map();
    sortedCats.forEach((cat) => {
      mainBuckets.set(cat.id, { cat, subjects: [] });
    });
    const extraBuckets = new Map();
    const uncategorized = { cat: { id: "uncategorized", name: "Uncategorized" }, subjects: [] };

    subjects.forEach((sub) => {
      const rawRoot = (sub.rootSubject || "Uncategorized").trim();
      const id = normalizeCategoryId(rawRoot) || "uncategorized";
      if (id === "uncategorized") { uncategorized.subjects.push(sub); return; }
      if (mainBuckets.has(id)) { mainBuckets.get(id).subjects.push(sub); return; }
      if (!extraBuckets.has(id)) extraBuckets.set(id, { cat: { id, name: rawRoot }, subjects: [] });
      extraBuckets.get(id).subjects.push(sub);
    });

    const rows = [];
    const push = (bucket) => {
      if (!bucket.subjects.length) return;
      bucket.subjects.sort((a, b) => (a.order || 0) - (b.order || 0));
      const qualified = currentUser
        ? bucket.subjects.filter(
            (s) =>
              s.tutorIds?.includes(currentUser.uid) ||
              currentUser.subjects?.includes(s.name)
          ).length
        : 0;
      rows.push({ name: bucket.cat.name, subjects: bucket.subjects, qualified });
    };

    for (const [, b] of mainBuckets) push(b);
    Array.from(extraBuckets.values())
      .sort((a, b) => a.cat.name.localeCompare(b.cat.name))
      .forEach(push);
    push(uncategorized);

    const totalQualified = rows.reduce((sum, r) => sum + r.qualified, 0);
    return { categoryRows: rows, totalQualified };
  }, [subjects, categories, currentUser]);

  const detailSubjects = useMemo(() => {
    if (!activeCategory) return [];
    const row = categoryRows.find((r) => r.name === activeCategory);
    if (!row) return [];
    const lower = searchTerm.toLowerCase();
    return searchTerm
      ? row.subjects.filter((s) => s.name.toLowerCase().includes(lower))
      : row.subjects;
  }, [activeCategory, categoryRows, searchTerm]);

  /* ─── Loading ─────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="tsm-page">
        <div className="tsm-loading">
          <div className="tsm-spinner" />
          <p className="tsm-loading-text">Loading subjects…</p>
        </div>
      </div>
    );
  }

  /* ─── Category detail view ───────────────────────────────────────────── */
  if (activeCategory) {
    const row = categoryRows.find((r) => r.name === activeCategory);
    return (
      <div className="tsm-page">
        <div className="tsm-container">
          {/* Detail header */}
          <div className="tsm-detail-header">
            <button className="tsm-back-btn" onClick={() => { setActiveCategory(null); setSearchTerm(""); }}>
              <FiChevronLeft size={18} />
            </button>
            <div className="tsm-detail-header-mid">
              <span className="tsm-detail-title">{activeCategory}</span>
              {row && (
                <span className="tsm-detail-count">
                  {row.qualified} of {row.subjects.length} qualified
                </span>
              )}
            </div>
          </div>

          {/* Search within category */}
          <div className="tsm-detail-search">
            <FiSearch size={15} className="tsm-search-icon" />
            <input
              className="tsm-search-input"
              placeholder="Search subjects…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="tsm-clear-btn" onClick={() => setSearchTerm("")}>
                <FiX size={13} />
              </button>
            )}
          </div>

          {/* Subject list */}
          <div className="tsm-detail-content">
            {detailSubjects.length === 0 && (
              <div className="tsm-empty">
                <p className="tsm-empty-title">No subjects found</p>
              </div>
            )}
            {detailSubjects.map((subject) => {
              const assigned =
                currentUser &&
                (subject.tutorIds?.includes(currentUser.uid) ||
                  currentUser.subjects?.includes(subject.name));
              return (
                <label
                  key={subject.id}
                  className={`tsm-subject-row ${assigned ? "tsm-subject-row--on" : ""}`}
                >
                  <span className="tsm-subject-name">{subject.name}</span>
                  <span className="tsm-checkbox-wrap">
                    <input
                      type="checkbox"
                      className="tsm-checkbox-input"
                      checked={!!assigned}
                      onChange={() => toggleAssignment(subject)}
                    />
                    <span className="tsm-checkbox-ui" aria-hidden="true" />
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  /* ─── Category list view (default) ──────────────────────────────────── */
  return (
    <div className="tsm-page">
      <div className="tsm-container">
        {/* Header */}
        <div className="tsm-list-header">
          <span className="tsm-list-title">My Subjects</span>
          <span className="tsm-total-badge">{totalQualified} qualified</span>
        </div>

        {/* Category rows */}
        <div className="tsm-cat-list">
          {categoryRows.length === 0 && (
            <div className="tsm-empty">
              <p className="tsm-empty-title">No subjects available</p>
            </div>
          )}
          {categoryRows.map((row) => (
            <button
              key={row.name}
              className="tsm-cat-row"
              onClick={() => setActiveCategory(row.name)}
            >
              <div className="tsm-cat-row-left">
                <span className="tsm-cat-row-name">{row.name}</span>
                <span className="tsm-cat-row-total">{row.subjects.length} subjects</span>
              </div>
              <div className="tsm-cat-row-right">
                {row.qualified > 0 && (
                  <span className="tsm-cat-row-qualified">
                    {row.qualified} qualified
                  </span>
                )}
                <FiChevronRight size={16} className="tsm-cat-row-chevron" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
